import {
    Alert,
    AlertDescription,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Icon,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import MiniStatistics from '../../components/card/MiniStatistics';
import IconBox from '../../components/icons/IconBox';
import PageLayout from '../../components/page-layout';
import PageCard from '../../components/page-card';
import EmptyState from '../../components/states/EmptyState';
import { ChartIllustration } from '../../components/illustrations';
import FadeIn from '../../components/animations/FadeIn';
import { StaggeredGrid, StaggeredItem } from '../../components/animations/StaggeredGrid';
import ScopePrompt from '../_shared/ScopePrompt';
import { classifyRead } from '../_shared/readState';
import { useSelectedLocation } from '../location-management/hooks/useSelectedLocation';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import { useApplyPricingChange } from './hooks/useApplyPricingChange';
import {
    PricingRecommendation,
    useGetPricingInsights,
} from './hooks/useGetPricingInsights';
import { formatPricePoint } from './utils/format-price-point';
import { MdTrendingDown, MdTrendingUp, MdAutoGraph, MdStar } from 'react-icons/md';
import { translate } from '../../i18n';
import { useGetCourts } from '../court-management/hooks/useGetCourts';
import { SportType } from '../../models/court/dto/court.dto';
import MultiCourtPicker from '../analytics/components/MultiCourtPicker';
import SportSelect from 'components/SportSelect';
import SkeletonCardGrid from '../../components/states/SkeletonCardGrid';

const t = (key: string) => translate(`pricing_insights.${key}` as any);

const REASON_LABELS: Record<string, () => string> = {
    LOW_DEMAND: () => t('low_demand'),
    BELOW_AVERAGE: () => t('below_average'),
    HIGH_DEMAND: () => t('high_demand'),
    PEAK_DEMAND: () => t('peak_demand'),
};

const REASON_COLORS: Record<string, string> = {
    LOW_DEMAND: 'red',
    BELOW_AVERAGE: 'orange',
    HIGH_DEMAND: 'green',
    PEAK_DEMAND: 'purple',
};

const CONFIDENCE_COLORS: Record<string, string> = {
    HIGH: 'green',
    MEDIUM: 'yellow',
    LOW: 'orange',
};

function formatCurrency(value: number, currency: string): string {
    const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;
    return `${symbol}${value.toFixed(2)}`;
}

export default function PricingInsightsPage() {
    const { selectedOrganization } = useSelectedOrganization();
    const { selectedLocation } = useSelectedLocation();
    const toast = useToast();

    const brandColor = useColorModeValue('brand.500', 'white');
    const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const subtextColor = useColorModeValue('secondaryGray.600', 'whiteAlpha.600');
    const tableBorderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const greenBg = useColorModeValue('green.100', 'green.800');
    const redBg = useColorModeValue('red.100', 'red.800');

    const [applyingKey, setApplyingKey] = useState<string | null>(null);
    const [selectedCourtIds, setSelectedCourtIds] = useState<number[]>([]);
    const [selectedSportTypes, setSelectedSportTypes] = useState<SportType[]>([]);

    const { data: courts = [] } = useGetCourts(selectedLocation?.id ?? '');
    const availableSports = useMemo(
        () => [...new Set(courts.flatMap((c) => c.sports ?? []))],
        [courts],
    );

    const { data, isInitialLoading, isError, error } = useGetPricingInsights(
        selectedLocation?.id ?? '',
        {
            courtIds: selectedCourtIds.length ? selectedCourtIds : undefined,
            sportTypes: selectedSportTypes.length ? selectedSportTypes : undefined,
        },
    );

    const readState = classifyRead(isError, isInitialLoading, data);

    const applyMutation = useApplyPricingChange(
        selectedOrganization?.id ?? '',
        selectedLocation?.id ?? '',
        (result) => {
            setApplyingKey(null);
            toast({
                title: `${t('price_updated')}: ${result.courtName}`,
                description: `${formatCurrency(result.oldPrice, result.currency)} → ${formatCurrency(result.newPrice, result.currency)} (${result.changePercent > 0 ? '+' : ''}${result.changePercent.toFixed(1)}%)`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
        },
        () => {
            setApplyingKey(null);
            toast({
                title: t('apply_error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    );

    const handleApply = (rec: PricingRecommendation) => {
        const key = rec.pricingIds.join(',');
        setApplyingKey(key);
        applyMutation.mutate({
            pricingIds: rec.pricingIds,
            newPrice: rec.recommendedPrice,
        });
    };

    if (!selectedLocation) {
        return (
            <PageLayout>
                <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                        {t('select_location')}
                    </AlertDescription>
                </Alert>
            </PageLayout>
        );
    }

    // `useGetPricingInsights` is `enabled: !!locationId`, so the raw
    // `isLoading` this branch used to read is `true` forever with no location
    // selected: four skeleton cards, permanently. The naive repair is worse
    // than the spinner — `recommendations.length === 0` renders the
    // `completion` EmptyState, i.e. "all your pricing is optimal", a
    // reassurance derived from a read nobody sent.
    if (readState === 'never-issued') {
        return (
            <PageLayout>
                <ScopePrompt gate='location' />
            </PageLayout>
        );
    }

    if (readState === 'loading') {
        return (
            <PageLayout>
                <SkeletonCardGrid cards={4} columns={{ base: 1, md: 2, xl: 4 }} />
            </PageLayout>
        );
    }

    if (error) {
        return (
            <PageLayout>
                <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>{t('load_error')}</AlertDescription>
                </Alert>
            </PageLayout>
        );
    }

    const summary = data?.summary;
    const recommendations = data?.recommendations ?? [];

    return (
        <PageLayout>
            {/* Summary cards with staggered entrance */}
            <StaggeredGrid staggerDelay={0.06}>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap="20px">
                    <StaggeredItem>
                        <MiniStatistics
                            startContent={
                                <IconBox
                                    w="56px"
                                    h="56px"
                                    bg={boxBg}
                                    icon={
                                        <Icon as={MdAutoGraph} w="32px" h="32px" color={brandColor} />
                                    }
                                />
                            }
                            name={t('courts_analyzed')}
                            value={summary?.totalCourts ?? 0}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <MiniStatistics
                            startContent={
                                <IconBox
                                    w="56px"
                                    h="56px"
                                    bg={boxBg}
                                    icon={
                                        <Icon as={MdStar} w="32px" h="32px" color={brandColor} />
                                    }
                                />
                            }
                            name={t('recommendations')}
                            value={summary?.recommendationCount ?? 0}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <MiniStatistics
                            startContent={
                                <IconBox
                                    w="56px"
                                    h="56px"
                                    bg={greenBg}
                                    icon={
                                        <Icon as={MdTrendingUp} w="32px" h="32px" color="green.500" />
                                    }
                                />
                            }
                            name={t('price_increases')}
                            value={summary?.increaseCount ?? 0}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <MiniStatistics
                            startContent={
                                <IconBox
                                    w="56px"
                                    h="56px"
                                    bg={redBg}
                                    icon={
                                        <Icon as={MdTrendingDown} w="32px" h="32px" color="red.500" />
                                    }
                                />
                            }
                            name={t('price_decreases')}
                            value={summary?.decreaseCount ?? 0}
                        />
                    </StaggeredItem>
                </SimpleGrid>
            </StaggeredGrid>

            {/* Filters */}
            <Flex gap="12px">
                <MultiCourtPicker
                    courts={courts}
                    selectedCourtIds={selectedCourtIds}
                    onChange={setSelectedCourtIds}
                />
                <SportSelect
                    mode="compact"
                    options={availableSports}
                    value={selectedSportTypes}
                    onChange={setSelectedSportTypes}
                    emptyLabel={translate('analytics.filters.all_sports')}
                    unitLabel={translate('analytics.filters.sports')}
                />
            </Flex>

            {/* Analysis period */}
            {summary && (
                <Text color={subtextColor} fontSize="sm">
                    {t('analysis_period')}: {new Date(summary.periodStartDate).toLocaleDateString()} — {new Date(summary.periodEndDate).toLocaleDateString()} ({summary.totalPricings} {t('pricing_slots_analyzed')})
                </Text>
            )}

            {/* Recommendations table */}
            <PageCard title={t('recommendations')}>
                {recommendations.length === 0 ? (
                    <EmptyState
                        variant="completion"
                        title={t('all_optimal')}
                    />
                ) : (
                    <FadeIn>
                        <Box overflowX="auto">
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <Th borderColor={tableBorderColor}>{t('court')}</Th>
                                        <Th borderColor={tableBorderColor}>{t('sport')}</Th>
                                        <Th borderColor={tableBorderColor}>{t('time_window')}</Th>
                                        <Th borderColor={tableBorderColor} isNumeric>{t('utilization')}</Th>
                                        <Th borderColor={tableBorderColor}>{t('reason')}</Th>
                                        <Th borderColor={tableBorderColor}>{t('confidence')}</Th>
                                        <Th borderColor={tableBorderColor} isNumeric>{t('current')}</Th>
                                        <Th borderColor={tableBorderColor} isNumeric>{t('recommended')}</Th>
                                        <Th borderColor={tableBorderColor} isNumeric>{t('change')}</Th>
                                        <Th borderColor={tableBorderColor}></Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {recommendations.map((rec) => (
                                        <Tr key={rec.pricingIds.join(',')}>
                                            <Td borderColor={tableBorderColor}>
                                                <Text color={textColor} fontWeight="600" fontSize="sm">
                                                    {rec.courtName}
                                                </Text>
                                            </Td>
                                            <Td borderColor={tableBorderColor}>
                                                <Text fontSize="sm">{rec.sport}</Text>
                                                <Text fontSize="xs" color={subtextColor}>
                                                    {formatPricePoint(
                                                        rec.capacity,
                                                        rec.slotDurationInMs,
                                                    )}
                                                </Text>
                                            </Td>
                                            <Td borderColor={tableBorderColor}>
                                                <Text fontSize="sm">
                                                    {t(`days.${rec.dayOfWeek}`)}{' '}
                                                    {rec.timeWindow.split(' ').pop()}
                                                </Text>
                                            </Td>
                                            <Td borderColor={tableBorderColor} isNumeric>
                                                <Text fontSize="sm">{rec.utilizationPercent.toFixed(1)}%</Text>
                                            </Td>
                                            <Td borderColor={tableBorderColor}>
                                                <Badge
                                                    colorScheme={REASON_COLORS[rec.reason] ?? 'gray'}
                                                    fontSize="xs"
                                                >
                                                    {REASON_LABELS[rec.reason]?.() ?? rec.reason}
                                                </Badge>
                                            </Td>
                                            <Td borderColor={tableBorderColor}>
                                                <Badge
                                                    colorScheme={CONFIDENCE_COLORS[rec.confidence] ?? 'gray'}
                                                    fontSize="xs"
                                                >
                                                    {rec.confidence}
                                                </Badge>
                                            </Td>
                                            <Td borderColor={tableBorderColor} isNumeric>
                                                <Text fontSize="sm">
                                                    {rec.priceRange
                                                        ? `${formatCurrency(rec.priceRange.min, rec.currency)} - ${formatCurrency(rec.priceRange.max, rec.currency)}`
                                                        : formatCurrency(rec.currentPrice, rec.currency)}
                                                </Text>
                                            </Td>
                                            <Td borderColor={tableBorderColor} isNumeric>
                                                <Text
                                                    fontSize="sm"
                                                    fontWeight="700"
                                                    color={rec.changePercent > 0 ? 'green.500' : 'red.500'}
                                                >
                                                    {formatCurrency(rec.recommendedPrice, rec.currency)}
                                                </Text>
                                            </Td>
                                            <Td borderColor={tableBorderColor} isNumeric>
                                                <Text
                                                    fontSize="sm"
                                                    color={rec.changePercent > 0 ? 'green.500' : 'red.500'}
                                                >
                                                    {rec.changePercent > 0 ? '+' : ''}
                                                    {rec.changePercent.toFixed(1)}%
                                                </Text>
                                            </Td>
                                            <Td borderColor={tableBorderColor}>
                                                <Button
                                                    size="xs"
                                                    variant="brand"
                                                    isLoading={applyingKey === rec.pricingIds.join(',')}
                                                    onClick={() => handleApply(rec)}
                                                >
                                                    {t('apply')}
                                                </Button>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    </FadeIn>
                )}
            </PageCard>
        </PageLayout>
    );
}
